import {
  useEffect,
  useMemo,
  useState,
} from "react";

import CashflowBreakdown from "../../components/director/CashflowBreakdown";
import CashflowMovementChart from "../../components/director/CashflowMovementChart";
import CashflowSummaryGrid from "../../components/director/CashflowSummaryGrid";
import CashflowTable from "../../components/director/CashflowTable";
import DirectorFilters from "../../components/director/DirectorFilters";
import QualityBanner from "../../components/director/QualityBanner";
import ScopeNotice from "../../components/director/ScopeNotice";
import { useDirectorActions, useDirectorContext } from "@contexts/director";
import { formatCompetence, formatFreshness } from "../../utils/directorFormat";

const safeDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const localCompetence = (value) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(safeDate(value));
  const read = (type) => parts.find((part) => part.type === type)?.value;
  return `${read("year")}-${read("month")}`;
};

const shiftMonth = (competence, delta) => {
  const [year, month] = competence.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const defaultCashflowRange = (asOf) => {
  const competence = localCompetence(asOf);
  return { from: shiftMonth(competence, -6), to: shiftMonth(competence, 18) };
};

const freshnessLabel = (status) => ({
  fresh: "atualizada",
  stale: "atrasada",
  critical_stale: "muito atrasada",
}[status] || "situação não classificada");

const CashFlow = () => {
  const state = useDirectorContext();
  const actions = useDirectorActions();
  const {
    selectedCompanyId,
    accessByCompany,
    metadata,
    period,
    filters,
    cashflow,
    cashflowBreakdown,
    cashflowBreakdownError,
    loading,
    error,
  } = state;
  const [selectedCompetence, setSelectedCompetence] = useState(null);
  const [scenario, setScenario] = useState("base");
  const access = accessByCompany[selectedCompanyId] || {};
  const defaultPeriod = useMemo(
    () => defaultCashflowRange(access.resolvedAt),
    [access.resolvedAt],
  );
  const hasPeriod = Boolean(period?.from && period?.to);
  const activePeriod = hasPeriod ? period : defaultPeriod;

  useEffect(() => {
    if (!selectedCompanyId) return;
    if (!hasPeriod) {
      actions?.setPeriod(defaultPeriod);
      return;
    }
    actions?.loadCashflow({ ...activePeriod, scenario });
  }, [
    actions,
    selectedCompanyId,
    hasPeriod,
    activePeriod.from,
    activePeriod.to,
    defaultPeriod,
    filters.legalEntityId,
    filters.projectKey,
    filters.costCenterId,
    scenario,
  ]);

  const competenceSignature = (cashflow?.months || [])
    .map((month) => month.competence)
    .join("|");
  const currentCompetence = localCompetence(access.resolvedAt);
  useEffect(() => {
    const competences = competenceSignature ? competenceSignature.split("|") : [];
    if (competences.length === 0) {
      setSelectedCompetence(null);
      return;
    }
    setSelectedCompetence((current) => {
      if (current && competences.includes(current)) return current;
      return competences.includes(currentCompetence) ? currentCompetence : competences[0];
    });
  }, [competenceSignature, currentCompetence]);

  useEffect(() => {
    if (!selectedCompanyId || !selectedCompetence) return;
    const visible = (cashflow?.months || []).some((month) => (
      month.competence === selectedCompetence
    ));
    if (visible) actions?.loadCashflowBreakdown(selectedCompetence, { scenario });
  }, [
    actions,
    selectedCompanyId,
    selectedCompetence,
    cashflow?.dataRevision,
    competenceSignature,
    scenario,
  ]);

  const breakdownMatches = cashflowBreakdown?.competence === selectedCompetence;
  const retry = () => {
    actions?.clearError();
    actions?.loadCashflow({ ...activePeriod, scenario });
  };
  const companyName = cashflow?.scope?.companyName || access.companyName || "Portfólio";

  return <main className="director-page">
    <header className="director-page__header">
      <div>
        <span className="director-eyebrow">Diretoria · projeção mensal</span>
        <h1>Projeção mensal de fluxo</h1>
        <p>{companyName} · {formatCompetence(activePeriod.from)} a {formatCompetence(activePeriod.to)} · sem saldo declarado</p>
      </div>
      {Object.keys(accessByCompany).length > 1 && <div className="director-filter-field">
        <label htmlFor="director-company">Empresa</label>
        <select
          id="director-company"
          value={selectedCompanyId}
          onChange={(event) => actions?.selectCompany(event.target.value)}
        >
          {Object.entries(accessByCompany).map(([companyId, companyAccess]) => (
            <option key={companyId} value={companyId}>{companyAccess.companyName}</option>
          ))}
        </select>
      </div>}
    </header>

    <DirectorFilters
      metadata={metadata || {}}
      period={activePeriod}
      filters={filters}
      onPeriodChange={actions?.setPeriod}
      onFiltersChange={actions?.setFilters}
    />
    <section className="director-module1__filters" aria-label="Cenário da projeção mensal">
      <label className="director-filter-field"><span>Cenário</span><select value={scenario} onChange={(event) => setScenario(event.target.value)}>
        <option value="base">Base</option>
        <option value="conservative">Conservador</option>
        <option value="stress">Estresse</option>
      </select></label>
      <span className="director-module1__badge">Regime A · fluxo sem saldo</span>
    </section>

    {error && <section className="director-state director-state--error" role="alert">
      <strong>{error.message || "Não foi possível carregar o fluxo de caixa."}</strong>
      {error.retryable && <button className="director-button" type="button" onClick={retry}>Tentar novamente</button>}
    </section>}
    {loading.cashflow && !cashflow && <section className="director-state" role="status">
      Carregando movimentos financeiros…
    </section>}
    {!loading.cashflow && cashflow?.status === "no_data" && <section className="director-state">
      Ainda não há movimentos para o período e o recorte selecionados.
    </section>}

    {cashflow && cashflow.status !== "no_data" && <>
      <QualityBanner
        flags={cashflow.qualityFlags}
        sourceIssueCodes={cashflow.sourceIssueCodes}
        sources={[cashflow]}
        status={cashflow.status}
      />
      <ScopeNotice
        coverage={cashflow.coverage}
        completePortfolioScope={access.completePortfolioScope}
        partialPoolingGroupIds={access.partialPoolingGroupIds}
      />
      <CashflowSummaryGrid summary={cashflow.summary} />
      <CashflowMovementChart months={cashflow.months || []} />

      <section className="director-section director-ledger" aria-labelledby="cashflow-ledger-title">
        <header>
          <div>
            <span className="director-eyebrow">Leitura por competência</span>
            <h2 id="cashflow-ledger-title">Movimentos mensais</h2>
          </div>
          <p>Selecione um mês para conferir os projetos e centros que compõem o movimento.</p>
        </header>
        <CashflowTable
          months={cashflow.months || []}
          selectedCompetence={selectedCompetence}
          onSelectCompetence={setSelectedCompetence}
        />
      </section>

      <CashflowBreakdown
        competence={selectedCompetence}
        error={cashflowBreakdownError}
        items={breakdownMatches ? cashflowBreakdown.items : []}
        loading={Boolean(loading.cashflowBreakdown) || (
          !breakdownMatches
          && Boolean(selectedCompetence)
          && !cashflowBreakdownError
        )}
        onRetry={() => actions?.loadCashflowBreakdown(selectedCompetence, { scenario })}
      />

      <footer className="director-freshness">
        Calculado em {formatFreshness(cashflow.calculatedAt)}.
        {(cashflow.freshness || []).map((item) => <span key={item.sourceType}>
          {` ${item.sourceType}: ${formatFreshness(item.extractedAt)} (${freshnessLabel(item.status)}).`}
        </span>)}
      </footer>
    </>}
  </main>;
};

export default CashFlow;
